import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Newspaper, 
  Gamepad2, 
  MessageSquare, 
  UserSearch, 
  Eye,
  Calendar,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface NewsItem {
  date: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const newsItems: NewsItem[] = [
  {
    date: '01/30/2025',
    title: 'Game Session Lookup',
    description: 'Look up any player\'s active game session by Gamertag or XUID. View all session members with their IP addresses and GeoIP data (country, city, ISP, coordinates).',
    icon: <Gamepad2 className="h-5 w-5" />,
  },
  {
    date: '01/30/2025',
    title: 'Profile OSINT Inspector',
    description: 'Comprehensive profile lookup tool with OctoSniff IP resolution. View gamerscore, account age, bio, friends list, achievements, clips, screenshots, clubs, activity feed, and resolved IP with GeoIP data.',
    icon: <UserSearch className="h-5 w-5" />,
  },
  {
    date: '01/30/2025',
    title: 'Party Chat Viewer',
    description: 'View your party or any friend\'s party members. See who\'s in the party with avatar, gamertag, XUID, and party creator info.',
    icon: <MessageSquare className="h-5 w-5" />,
  },
];

const quickLinks = [
  { to: '/party-chats', icon: <MessageSquare className="h-6 w-6" />, label: 'Party Chats', description: 'View party members' },
  { to: '/games', icon: <Gamepad2 className="h-6 w-6" />, label: 'Game Sessions', description: 'Lookup game sessions & IPs' },
  { to: '/profile-inspector', icon: <UserSearch className="h-6 w-6" />, label: 'Profile Inspector', description: 'OSINT lookup tool' },
  { to: '/presence', icon: <Eye className="h-6 w-6" />, label: 'Presence', description: 'Control online status' },
];

const Dashboard: React.FC = () => {

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Dashboard</h1>
        <p className="text-muted-foreground mt-2">
          Welcome to OctoSniff Xbox Toolkit
        </p>
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {quickLinks.map((link) => (
          <Link key={link.to} to={link.to}>
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/10 text-primary">
                    {link.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{link.label}</h3>
                    <p className="text-sm text-muted-foreground">{link.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* News Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Newspaper className="h-5 w-5" />
            Latest Updates
          </CardTitle>
          <CardDescription>
            Recent changes and new features
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {newsItems.map((item, index) => (
              <div
                key={index}
                className="flex flex-col gap-3 rounded-lg border border-border bg-card/50 p-4 transition-colors hover:bg-accent/20 sm:flex-row sm:gap-4"
              >
                <div className="flex-shrink-0 p-2 rounded-lg bg-green-500/10 text-green-500 h-fit">
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-foreground">{item.title}</h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border bg-green-500/10 text-green-500 border-green-500/30">
                      <Sparkles className="h-3 w-3" />
                      New Feature
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {item.date}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Footer Info */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-6">
          <div className="text-center">
            <h3 className="font-semibold text-primary mb-2">OctoSniff Xbox Toolkit</h3>
            <p className="text-sm text-muted-foreground">
              Xbox Live API toolkit for profile inspection, party management, game session lookup, and more.
              All API calls are proxied through the backend for security.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
